import {Component, EventEmitter, Input, Output, ViewChild} from '@angular/core';
import {FileUpload} from 'primeng/fileupload';
import {AnimalModel} from 'src/app/modules/animals/models/animal.model';
import {WaitingDialogService} from '../../services/waiting-dialog.service';
import {AnimalImagesRepository} from 'src/app/modules/animals/states/animal-images.repository';
import {ToastService} from '../../services/toast.service';
import {AnimalMediaCategoryModel} from 'src/app/modules/animals/models/animal-media-category.model';
import {AnimalImageService} from 'src/app/modules/animals/services/animal-image.service';
import {AnimalVideoRepository} from "../../../modules/animals/states/animal-video.repository";
import {catchError, concatMap, from, map, of, toArray} from 'rxjs';


@Component({
  selector: 'app-media-upload',
  templateUrl: './media-upload.component.html',
  styleUrls: ['./media-upload.component.scss'],
  standalone: false
})
export class MediaUploadComponent {
  @ViewChild('fileUpload') fileUpload!: FileUpload;
  @Input() animal: AnimalModel | null = null;
  @Output() mediaUploaded = new EventEmitter<number[]>();
  selectedFiles: any[] = [];
  dialogVisible = false;
  mediaCategoryOptions: AnimalMediaCategoryModel[] = [];


  constructor(
    private readonly _waitingDialogService: WaitingDialogService,
    private readonly _animalImagesRep: AnimalImagesRepository,
    private readonly _animalVideosRep: AnimalVideoRepository,
    private readonly _toastService: ToastService,
    private readonly _animalImageService: AnimalImageService
  ) {
  }

  onFileSelect(): void {
    const files: File[] = this.fileUpload?.files ?? [];
    if (!files.length) return;

    this.selectedFiles = files.map((file: File) => ({
      file,
      objectURL: URL.createObjectURL(file),
      description: '',
      externalDescription: '',
      category: [],
      syncWithSharedFolder: false,
      syncWithWebsite: false,
      fileType: file.type
    }));

    this.dialogVisible = true;
  }

  openDialog(): void {
    this.mediaCategoryOptions = this._animalImageService.getAnimalImageCategories();
    this.dialogVisible = true;
  }

  onClearSelection(): void {
    this.selectedFiles = [];
    this.fileUpload?.clear();
  }

  onUploadHandler(event: any): void {

  }

  onDialogHide() {
    this.onClearSelection();
  }

  uploadFiles(): void {
    if (!this.animal) return;

    this._waitingDialogService.showWaitingDialog(true);

    // Uploads nacheinander senden: parallele POSTs auf dasselbe Tier führen im Backend zu Deadlocks
    from(this.selectedFiles).pipe(
      concatMap(fileEntry => this.uploadFile(fileEntry).pipe(
        map((response: any) => ({fileEntry, id: response?.id as number | undefined})),
        catchError(() => of({fileEntry, id: undefined}))
      )),
      toArray()
    ).subscribe(results => {
      const uploadedIds = results.filter(r => r.id != null).map(r => r.id as number);
      const failedFiles = results.filter(r => r.id == null).map(r => r.fileEntry);

      this._waitingDialogService.showWaitingDialog(false);

      if (uploadedIds.length) {
        this.mediaUploaded.emit(uploadedIds);
      }

      if (!failedFiles.length) {
        this._toastService.showSuccess('Dateien erfolgreich hochgeladen.');
        this.fileUpload.clear();
        this.dialogVisible = false;
        this.onClearSelection();
        return;
      }

      // Nur die fehlgeschlagenen Dateien im Dialog lassen, damit ein erneuter Versuch keine Duplikate erzeugt
      this.selectedFiles = failedFiles;
      this._toastService.showError(`Fehler beim Hochladen von ${failedFiles.length} von ${results.length} Dateien.`);
    });
  }

  private uploadFile(fileEntry: any) {
    const animalId = this.animal!.id;
    const formData = new FormData();
    formData.append('file', fileEntry.file);
    formData.append('animal_id', animalId.toString());

    if (fileEntry.description) {
      formData.append('description', fileEntry.description);
    }

    if (fileEntry.externalDescription) {
      formData.append('externalDescription', fileEntry.externalDescription);
    }

    if (fileEntry.category?.length) {
      for (const cat of fileEntry.category) {
        formData.append('categories[]', cat);
      }
    }

    if (fileEntry.syncWithSharedFolder) {
      formData.append('syncWithSharedFolder', 'true');
    }

    if (fileEntry.syncWithWebsite) {
      formData.append('syncWithWebsite', 'true');
    }

    const isImage = fileEntry.file.type.startsWith('image/');
    return isImage
      ? this._animalImagesRep.addImage(formData, animalId)
      : this._animalVideosRep.addVideo(formData, animalId);
  }

  isImage(file: any): boolean {
    return file.file?.type?.startsWith('image/');
  }

  removeFile(index: number): void {
    this.selectedFiles.splice(index, 1);

    const remainingFiles = this.selectedFiles.map(f => f.file);
    this.fileUpload.clear();
    if (remainingFiles.length) {
      Object.defineProperty(this.fileUpload, 'files', {
        value: remainingFiles,
        writable: true
      });
      this.onFileSelect();
    }
  }
}
